import React, { useState } from 'react';
import styled from '@emotion/styled';
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Button,
  Select,
  MenuItem
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Check';
import { FloorLevel, HousingType, Roommate } from '../types';
import AddressInput from '@/components/common/AddressInput';
import { HabitationAddressFields } from './HabitationAddressFields';

const EMPTY_ADDRESS: Address = {
  street: '',
  postalCode: '',
  city: '',
  country: '',
};

import { useAppDispatch } from '@/redux/hooks';
import { formatDateOnly } from '@/utils/date';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { addRoommate, removeRoommate, setAddress, setFloorLevel, setHousingType, setNumberOfRooms } from '../habitationSlice';
import { useTranslation } from 'react-i18next';
import FloorIcon from './FloorIcon';
import DeleteIcon from '@mui/icons-material/Close';
import RoommateFormModal from './RoommateFormModal';
import type { SelectChangeEvent } from '@mui/material/Select';

interface Address {
  street: string;
  postalCode: string;
  city: string;
  country: string;
}

interface HousingRecapBoxProps {
  isRoom: boolean;
  floorLevel: FloorLevel | null;
  address: Address | null;
  roommates?: Roommate[];
  numberOfRooms: number;
  housingType: HousingType | null;
  onEdit?: () => void;
  onSave?: (updated: { address: Address; floorLevel: FloorLevel }) => void;
}

const OptionBox = styled(Box)<{ selected: boolean }>`
  border: 2px solid ${({ selected }) => (selected ? '#2F6472' : '#ccc')};
  border-radius: 2px;
  padding: 2px;
  text-align: center;
  cursor: pointer;
  position: relative;
  height: 80px;
  width: 80px;
  &:hover {
    border-color: #2F6472;
  }
  @media (max-width: 600px) {
      height: 40px;
      width: 40px;
      padding: 0px;
      & div {
        height: 40px;
      }
      & div > div {
        height: 3px;
        width: 80%;
      }
  }
`;

const TypographyStyled = styled(Typography)`
  margin-top: 4px;
  color: white;
  text-transform: uppercase;
  font-weight: 500;
  @media (max-width: 600px) {
    font-size: 12px;
  }
`

const TypographyFloorStyled = styled(Typography)`
  text-align: center;
  @media (max-width: 600px) {
    font-size: 9px;
    line-height: 1.2;
  }
`


const Spacing = styled.div`
  height: 5px;
  @media (max-width: 600px) {
    height: 2px;
  }
`;

const IconButtonStyled = styled(IconButton)`
    padding: 4px;
    margin: 10px;
    color: white;
    background-color: #8CCDCD;
    &:hover {
        background-color: #2F6472;
    }
    @media (max-width: 600px) {
        margin: 8px;
    }
`

const RoommateLine = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  color: white;
`;


const ButtonAddColoc = styled(Button)`
    color: white;
    border: solid, white;
    color: white;
    padding: 5px 20px;
    &:hover {
        background-color: white;
        color: #2F6472;
    }
`;

const AddIconStyled = styled(AddIcon)`
    color: #8CCDCD;
    background-color: white;
    border-radius: 100%;
`;


const HousingRecapBox: React.FC<HousingRecapBoxProps> = ({
  isRoom,
  floorLevel,
  address,
  roommates,
  numberOfRooms,
  housingType,
  onSave
}) => {
  const dispatch = useAppDispatch();
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const { t } = useTranslation('habitation');

  const floorLevelOptions: { label: string; value: FloorLevel }[] = [
    { label: 'Rez-de-chaussée', value: FloorLevel.ground },
    { label: 'Étage intermédiaire', value: FloorLevel.middle },
    { label: 'Dernier étage', value: FloorLevel.top },
  ];
  const [roommateData, setRoommateData] = useState<Roommate>({
    civility: 'M',
    firstname: '',
    lastname: '',
    birthdate: ''
  });

  const housingTypeOptions = [ t('step2.room'), t('step2.entire')];

  const [modalRoommateOpen, setModalRoommateOpen] = useState(false);
  const handleAddRoommate = () => {
    if (roommateData.firstname && roommateData.lastname && roommateData.birthdate) {
      dispatch(addRoommate(roommateData));
      setModalRoommateOpen(false);
      setRoommateData({ civility: 'M', firstname: '', lastname: '', birthdate: '' });
    }
  };
  
  const isAddressValid = (addr = address) => {
      return !!addr?.street && !!addr?.postalCode && !!addr?.city;
    };


  const handleRoomChange = (delta: number) => {
    dispatch(setNumberOfRooms(Math.max(1, numberOfRooms + delta)));
  };

  const handleCheck = (switchEdit: boolean) => {
    if (!isAddressValid()) {
      setError(t('step1.address.error') || 'Merci de renseigner une adresse complète.');
      return;
    }
    else {
        setError('');
        setEditing(switchEdit);
        onSave && onSave({ address: address as Address, floorLevel: floorLevel as FloorLevel });
    }

  };

  const handleSelectFloor = (level: FloorLevel) => {
      dispatch(setFloorLevel(level));
    };
  

  const handleSelect = (event: SelectChangeEvent) => {
    dispatch(setHousingType(event.target.value as unknown as HousingType));
  };

  return (
    <>
      <Box sx={{ backgroundColor: '#aee0dd', borderRadius: 2, p: 2, position: 'relative', minHeight : '200px' }}>
        <TypographyStyled variant="h5" sx={{ mb: 1 }}>
          {t('step5.recap')}
        </TypographyStyled>
        <IconButton
          size="small"
          sx={{ position: 'absolute', top: 8, right: 8 }}
          onClick={() => handleCheck(!editing)}
        >
          {editing ? <SaveIcon /> : <EditIcon />}
        </IconButton>
        <Spacing />
        {editing ? (
          <Box>
            <TypographyStyled sx={{ textTransform: 'uppercase', fontWeight: 500, mb: 1 }}>
              {t('step5.room_type')} :
            </TypographyStyled>
            <Select
              value={housingType?.toString()}
              onChange={handleSelect}
              sx={{
                background: 'transparent',
                color: 'white',
                border: 'white',
                fontWeight: 500,
                borderRadius: 1,
                minWidth: 180,
                textTransform: 'uppercase',
              }}
              size="small"
            >
              <MenuItem value={HousingType.room} sx={{ textTransform: 'uppercase' }}>{t('step2.room')}</MenuItem>
              <MenuItem value={HousingType.full} sx={{ textTransform: 'uppercase' }}>{t('step2.entire')}</MenuItem>
            </Select>
          </Box>
        ) : (
          <TypographyStyled sx={{ textTransform: 'uppercase', fontWeight: 500 }}>
            {t('step5.room_type')} :  <span style={{fontWeight: 'normal'}}>{housingTypeOptions[housingType || 0]}</span>
          </TypographyStyled>
        )}
        <Spacing />
          {!isRoom && numberOfRooms && (
          <Grid container spacing={0} mt={0} alignItems={'anchor-center'}>
            <Grid item xs={7} sm={8} >
              <TypographyStyled sx={{ textTransform: 'uppercase' }}>
                 {t('step3.rooms_title')} :
              </TypographyStyled>
            </Grid>
            <Grid container item xs={5} sm={4}  >
              <IconButtonStyled sx={{display: !editing? 'none' : 'inherit'}} onClick={() => handleRoomChange(-1)}><RemoveIcon/></IconButtonStyled>
                  <Typography sx={{margin: 'auto 0', color: 'white'}} fontSize={19} >{numberOfRooms}</Typography>
              <IconButtonStyled  sx={{display: !editing? 'none' : 'inherit'}} onClick={() => handleRoomChange(1)}><AddIcon /></IconButtonStyled>
            </Grid>
          </Grid>
          )}
        <Spacing />
        {editing ? (
          <Grid container spacing={0} mt={0}>
            <Grid item xs={12} sm={12} >
              <TypographyStyled sx={{ textTransform: 'uppercase' }}>
                {t('step5.floor')} :
              </TypographyStyled>
            </Grid>
            <Grid item xs={4} sm={4} container justifyContent="center" textAlign="center" >
              <OptionBox selected={floorLevel === FloorLevel.ground} onClick={() => handleSelectFloor(FloorLevel.ground)}>
                <FloorIcon highlightedIndex={0} />
              </OptionBox>
              <TypographyFloorStyled mt={{sm: 1, xs: 0}}>{t('step3.ground')}</TypographyFloorStyled>
            </Grid>
            <Grid item xs={4} sm={4} container justifyContent="center" >
              <OptionBox selected={floorLevel === FloorLevel.middle} onClick={() => handleSelectFloor(FloorLevel.middle)}>
                <FloorIcon highlightedIndex={1} />
              </OptionBox>
              <TypographyFloorStyled mt={{sm: 1, xs: 0}}>{t('step3.middle')}</TypographyFloorStyled>
            </Grid>
            <Grid item xs={4} sm={4} container justifyContent="center" sx={{display:'grid'}}>
              <OptionBox selected={floorLevel === FloorLevel.top} onClick={() => handleSelectFloor(FloorLevel.top)} sx={{margin:'auto'}}>
                <FloorIcon highlightedIndex={2} />
              </OptionBox>
              <TypographyFloorStyled mt={{sm: 1, xs: 0}}>{t('step3.last')}</TypographyFloorStyled>
            </Grid>
          </Grid>
        ) : (
          <TypographyStyled sx={{ textTransform: 'uppercase' }}>
            {t('step5.floor')} : <span style={{fontWeight: 'normal'}}>{floorLevelOptions.find(option => option.value === floorLevel)?.label || 'Non spécifié'}</span>
          </TypographyStyled>
        )}
        <Spacing />
        {editing ? (
        <Grid container spacing={0} mt={0}>
          <Grid item xs={12} sm={12} >
            <TypographyStyled sx={{ textTransform: 'uppercase' }}>
              {t('step5.address')} :
            </TypographyStyled>
          </Grid>
          <Grid item xs={12} sm={12} >
            {/* `limitFrenchCountry` : adresse du LOGEMENT assuré, et Studcorp
                n'assure qu'en France. Voir AddressStep. */}
            <HabitationAddressFields>
              <AddressInput
                value={address ?? EMPTY_ADDRESS}
                onChange={(addr) => dispatch(setAddress(addr))}
                limitFrenchCountry
              />
            </HabitationAddressFields>
          </Grid>
        </Grid>
        ) : (
          <>
            <TypographyStyled sx={{ textTransform: 'uppercase' }}>
              {t('step5.address')} :
            </TypographyStyled>
          <TypographyStyled >
            {address?.street}, {address?.postalCode} {address?.city}, {address?.country}
          </TypographyStyled>
          </>
        )}
        <Spacing />
          {!isRoom && roommates && roommates.length > 0 && (
          <Grid container spacing={0} mt={0}>
            <Grid item xs={12} sm={12} >
              <TypographyStyled sx={{ textTransform: 'uppercase' }}>
                 {t('step5.roommate')} :
              </TypographyStyled>
            </Grid>
            {roommates.map((r: Roommate, i: number) => (
              <RoommateLine key={i}>
                {r.civility} {r.firstname} {r.lastname} {t('step3.flatmate_brithday')}{' '}
                {r.birthdate
                  ? formatDateOnly(r.birthdate)
                  : ''}
                <IconButton onClick={() => dispatch(removeRoommate(i))} size="small" color="error">
                  <DeleteIcon sx={{display: !editing? 'none' : 'inherit'}} fontSize="small" />
                </IconButton>
              </RoommateLine>
            ))}
          </Grid>
          )}
          {!isRoom && editing && (
          <Grid item xs={12} sm={12} container justifyContent="left" alignItems="left" mt={2}>
              <Box mb={1}>
                  <ButtonAddColoc
                      startIcon={<AddIconStyled />}
                      onClick={() => setModalRoommateOpen(true)}
                      >
                  {t('step3.add_flatmate')}
                  </ButtonAddColoc>
              </Box>
          </Grid>
           )}
        {/* Modal ajout coloc */}
        <RoommateFormModal
          open={modalRoommateOpen}
          onClose={() => setModalRoommateOpen(false)}
          onSubmit={handleAddRoommate}
          roommateData={roommateData}
          setRoommateData={setRoommateData}
          t={t}
        />
      </Box>
    </>
  );
};

export default HousingRecapBox;